import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  User, Mail, Phone, Calendar, Briefcase, 
  ChevronDown, ChevronUp, Clock, CheckCircle, XCircle,
  Eye, FileText, GraduationCap, Award, Users, Download,
  Search, Filter, ArrowUpDown, FileSpreadsheet, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [positionFilter, setPositionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('submitted_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/applications`, {
        headers: getAuthHeaders()
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appId, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/applications/${appId}/status?status=${newStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      setApplications(apps => 
        apps.map(app => app.id === appId ? { ...app, status: newStatus } : app)
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Get unique positions for filter dropdown
  const uniquePositions = useMemo(() => {
    const positions = [...new Set(applications.map(app => app.position_applied))];
    return positions.sort();
  }, [applications]);

  // Filter and sort applications
  const filteredApplications = useMemo(() => {
    let result = [...applications];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(app => 
        `${app.first_name} ${app.last_name}`.toLowerCase().includes(query) ||
        app.email.toLowerCase().includes(query) ||
        app.telephone.includes(query) ||
        app.position_applied.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(app => app.status === statusFilter);
    }

    // Position filter
    if (positionFilter !== 'all') {
      result = result.filter(app => app.position_applied === positionFilter);
    }

    // Date range filter
    if (dateRange.start) {
      result = result.filter(app => new Date(app.submitted_at) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
      result = result.filter(app => new Date(app.submitted_at) <= new Date(dateRange.end + 'T23:59:59'));
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
          bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case 'position':
          aVal = a.position_applied.toLowerCase();
          bVal = b.position_applied.toLowerCase();
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        default:
          aVal = new Date(a.submitted_at);
          bVal = new Date(b.submitted_at);
      }
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return result;
  }, [applications, searchQuery, statusFilter, positionFilter, dateRange, sortField, sortDirection]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPositionFilter('all');
    setDateRange({ start: '', end: '' });
    setSortField('submitted_at');
    setSortDirection('desc');
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || positionFilter !== 'all' || dateRange.start || dateRange.end;

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'ID', 'First Name', 'Last Name', 'Email', 'Phone', 'Position', 'Position Type',
      'Status', 'Submitted Date', 'City', 'State', 'Education Level',
      'Has Experience', 'CPR Certified', 'Driver License'
    ];
    
    const csvData = filteredApplications.map(app => [
      app.id,
      app.first_name,
      app.last_name,
      app.email,
      app.telephone,
      app.position_applied,
      app.position_type,
      app.status,
      new Date(app.submitted_at).toLocaleDateString(),
      app.city,
      app.state,
      app.education_level,
      app.has_human_services_experience,
      app.is_cpr_certified,
      app.has_driver_license
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `job_applications_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const generatePDF = (app) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let y = 20;
    const lineHeight = 7;

    const addSectionHeader = (title) => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFillColor(212, 175, 55);
      doc.rect(margin, y, pageWidth - 2 * margin, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin + 3, y + 6);
      doc.setTextColor(0, 0, 0);
      y += 12;
    };

    const addField = (label, value) => {
      if (y > 275) { doc.addPage(); y = 20; }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, margin, y);
      doc.setFont('helvetica', 'normal');
      const valueText = value || 'N/A';
      const splitText = doc.splitTextToSize(valueText.toString(), pageWidth - margin - 70);
      doc.text(splitText, 70, y);
      y += Math.max(lineHeight, splitText.length * 5);
    };

    // Header
    doc.setFillColor(55, 65, 81);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(212, 175, 55);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('LEHIGH HUMAN SUPPORT SERVICES', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('Job Application', pageWidth / 2, 25, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    y = 45;

    // Position Applied - Prominently at the top
    doc.setFillColor(212, 175, 55);
    doc.rect(margin, y, pageWidth - 2 * margin, 12, 'F');
    doc.setTextColor(55, 65, 81);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`POSITION: ${app.position_applied}`, pageWidth / 2, y + 8, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    y += 18;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text(`Application ID: ${app.id}`, margin, y);
    doc.text(`Status: ${app.status?.toUpperCase() || 'NEW'}`, pageWidth - margin - 40, y);
    doc.text(`Submitted: ${new Date(app.submitted_at).toLocaleDateString()}`, margin, y + 5);
    y += 15;

    addSectionHeader('APPLICANT INFORMATION');
    addField('Full Name', `${app.first_name} ${app.middle_name || ''} ${app.last_name}`);
    if (app.preferred_name) addField('Preferred Name', app.preferred_name);
    addField('Date of Birth', app.date_of_birth);
    addField('Email', app.email);
    addField('Phone', app.telephone);
    addField('Address', `${app.address_line1}${app.address_line2 ? ', ' + app.address_line2 : ''}`);
    addField('City/State/ZIP', `${app.city}, ${app.state} ${app.zip_code}`);
    y += 5;

    addSectionHeader('EMERGENCY CONTACT');
    addField('Name', app.emergency_contact_name);
    addField('Relationship', app.emergency_relationship);
    addField('Phone', app.emergency_phone);
    y += 5;

    addSectionHeader('POSITION DETAILS');
    addField('Position Type', app.position_type);
    addField('Earliest Start Date', app.earliest_start_date);
    addField('Days Available', app.days_available?.join(', '));
    addField('Preferred Shifts', app.preferred_shifts?.join(', '));
    addField('Desired Pay Rate', app.desired_pay_rate || 'Not specified');
    y += 5;

    addSectionHeader('PRE-EMPLOYMENT ELIGIBILITY');
    addField("Driver's License", app.has_driver_license);
    addField('Reliable Transportation', app.has_reliable_transportation);
    addField('Veteran', app.is_veteran);
    addField('Criminal Conviction', app.has_criminal_conviction);
    addField('Background Check', app.willing_background_check);
    y += 5;

    doc.addPage();
    y = 20;

    addSectionHeader('EMPLOYMENT HISTORY');
    doc.setFont('helvetica', 'bold');
    doc.text('Employer 1:', margin, y);
    y += lineHeight;
    addField('Company', app.emp1_name);
    addField('Address', `${app.emp1_address}, ${app.emp1_city}, ${app.emp1_state} ${app.emp1_zip}`);
    addField('Title', app.emp1_title);
    addField('Dates', app.emp1_dates);
    addField('Pay Rate', app.emp1_pay_rate);
    addField('Supervisor', `${app.emp1_supervisor} (${app.emp1_supervisor_phone})`);
    addField('Reason for Leaving', app.emp1_reason_leaving);
    y += 5;

    if (app.emp2_name) {
      doc.setFont('helvetica', 'bold');
      doc.text('Employer 2:', margin, y);
      y += lineHeight;
      addField('Company', app.emp2_name);
      addField('Title', app.emp2_title);
      addField('Dates', app.emp2_dates);
      y += 5;
    }

    addSectionHeader('EDUCATION');
    addField('Highest Level', app.education_level);
    addField('Human Services Exp.', app.has_human_services_experience);
    addField('School Name', app.school1_name);
    addField('Degree', `${app.school1_degree} in ${app.school1_major}`);
    y += 5;

    addSectionHeader('CERTIFICATIONS');
    addField('Professional License', app.has_professional_license);
    addField('CPR Certified', app.is_cpr_certified);
    if (app.certifications_list) addField('Certifications', app.certifications_list);
    y += 5;

    addSectionHeader('PROFESSIONAL REFERENCES');
    addField('Reference 1', `${app.ref1_name} - ${app.ref1_phone} - ${app.ref1_email}`);
    addField('Reference 2', `${app.ref2_name} - ${app.ref2_phone} - ${app.ref2_email}`);
    addField('Reference 3', `${app.ref3_name} - ${app.ref3_phone} - ${app.ref3_email}`);
    y += 10;

    addSectionHeader('CERTIFICATION');
    addField('Signature', app.certification_signature);
    addField('Date Signed', app.certification_date);

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
    }

    doc.save(`Application_${app.last_name}_${app.first_name}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      reviewing: 'bg-amber-100 text-amber-800',
      interviewed: 'bg-purple-100 text-purple-800',
      hired: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || styles.new}>{status.toUpperCase()}</Badge>;
  };

  const formatDate = (dateStr) => {
    // Convert UTC timestamp to local timezone
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-applications">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Job Applications</h2>
          <p className="text-gray-600 mt-2">
            {filteredApplications.length} of {applications.length} applications
            {hasActiveFilters && ' (filtered)'}
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={exportToCSV}
            variant="outline"
            className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            data-testid="export-csv-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="p-4">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search by name, email, phone, or position..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>

          {/* Filter Row */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Filters:</span>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="reviewing">Reviewing</option>
              <option value="interviewed">Interviewed</option>
              <option value="hired">Hired</option>
              <option value="rejected">Rejected</option>
            </select>

            {/* Position Filter */}
            <select
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              data-testid="position-filter"
            >
              <option value="all">All Positions</option>
              {uniquePositions.map(pos => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>

            {/* Date Range */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="date-start"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="date-end"
              />
            </div>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600">
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Sort Options */}
          <div className="flex gap-2 items-center">
            <ArrowUpDown className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Sort by:</span>
            {[
              { field: 'submitted_at', label: 'Date' },
              { field: 'name', label: 'Name' },
              { field: 'position', label: 'Position' },
              { field: 'status', label: 'Status' }
            ].map(({ field, label }) => (
              <Button
                key={field}
                variant={sortField === field ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleSort(field)}
                className={sortField === field ? 'bg-amber-500 hover:bg-amber-600' : ''}
              >
                {label}
                {sortField === field && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Applications List */}
      {filteredApplications.length === 0 ? (
        <Card className="p-12 text-center">
          <CardContent>
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Applications Found</h3>
            <p className="text-gray-500">
              {hasActiveFilters 
                ? 'No applications match your current filters.'
                : 'No job applications have been submitted yet.'}
            </p>
            {hasActiveFilters && (
              <Button onClick={clearFilters} className="mt-4" variant="outline">
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredApplications.map(app => (
            <Card key={app.id} className="overflow-hidden" data-testid={`application-${app.id}`}>
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedId(expandedId === app.id ? null : app.id)}
              >
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <User className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                        {app.first_name} {app.middle_name || ''} {app.last_name}
                        {getStatusBadge(app.status)}
                      </CardTitle>
                      <div className="text-sm text-gray-600 mt-1 space-y-1">
                        <div className="flex items-center gap-4 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" /> {app.position_applied}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" /> {app.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" /> {app.telephone}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <Clock className="w-4 h-4" /> Submitted: {formatDate(app.submitted_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm">
                      {expandedId === app.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </Button>
                  </div>
                </div>
              </CardHeader>

              {expandedId === app.id && (
                <CardContent className="border-t bg-gray-50 pt-6">
                  {/* Position Applied - Prominent Banner at Top */}
                  <div className="mb-6 p-4 bg-amber-500 rounded-lg text-center">
                    <h3 className="text-2xl font-bold text-white flex items-center justify-center gap-2">
                      <Briefcase className="w-6 h-6" />
                      {app.position_applied}
                    </h3>
                    <p className="text-amber-100 mt-1">{app.position_type} • Earliest Start: {app.earliest_start_date}</p>
                  </div>

                  {/* Actions */}
                  <div className="mb-6 p-4 bg-white rounded-lg border">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3">
                      <h4 className="font-semibold">Update Status</h4>
                      <div className="flex gap-2">
                        {app.resume_filename && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`${API_URL}/api/admin/applications/${app.id}/resume`, '_blank')}
                            className="bg-amber-500 text-white hover:bg-amber-600"
                            data-testid={`download-resume-${app.id}`}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Resume
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generatePDF(app)}
                          className="bg-gray-800 text-white hover:bg-gray-700"
                          data-testid={`download-pdf-${app.id}`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {['new', 'reviewing', 'interviewed', 'hired', 'rejected'].map(status => (
                        <Button
                          key={status}
                          variant={app.status === status ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => updateStatus(app.id, status)}
                          className={app.status === status ? 'bg-amber-500' : ''}
                        >
                          {status === 'hired' && <CheckCircle className="w-4 h-4 mr-1" />}
                          {status === 'rejected' && <XCircle className="w-4 h-4 mr-1" />}
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Application Details Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Personal Information */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <User className="w-5 h-5 text-amber-500" /> Personal Information
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Full Name:</strong> {app.first_name} {app.middle_name || ''} {app.last_name}</p>
                        {app.preferred_name && <p><strong>Preferred Name:</strong> {app.preferred_name}</p>}
                        <p><strong>Date of Birth:</strong> {app.date_of_birth}</p>
                        <p><strong>Email:</strong> {app.email}</p>
                        <p><strong>Phone:</strong> {app.telephone}</p>
                        <p><strong>Address:</strong> {app.address_line1}{app.address_line2 && `, ${app.address_line2}`}</p>
                        <p>{app.city}, {app.state} {app.zip_code}</p>
                      </div>
                    </div>

                    {/* Emergency Contact */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Phone className="w-5 h-5 text-amber-500" /> Emergency Contact
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Name:</strong> {app.emergency_contact_name}</p>
                        <p><strong>Relationship:</strong> {app.emergency_relationship}</p>
                        <p><strong>Phone:</strong> {app.emergency_phone}</p>
                      </div>
                    </div>

                    {/* Availability & Schedule Details */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-amber-500" /> Availability & Schedule
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Days Available:</strong> {app.days_available?.join(', ')}</p>
                        <p><strong>Preferred Shifts:</strong> {app.preferred_shifts?.join(', ')}</p>
                        <p><strong>Desired Pay:</strong> {app.desired_pay_rate || 'Not specified'}</p>
                      </div>
                    </div>

                    {/* Pre-Employment */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Eye className="w-5 h-5 text-amber-500" /> Pre-Employment
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Driver&apos;s License:</strong> {app.has_driver_license}</p>
                        <p><strong>Reliable Transportation:</strong> {app.has_reliable_transportation}</p>
                        <p><strong>Veteran:</strong> {app.is_veteran}</p>
                        <p><strong>Background Check:</strong> {app.willing_background_check}</p>
                      </div>
                    </div>

                    {/* Employment History */}
                    <div className="bg-white p-4 rounded-lg border lg:col-span-2">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-amber-500" /> Employment History
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="p-3 bg-gray-50 rounded">
                          <p className="font-medium text-amber-600 mb-2">Employer 1</p>
                          <p><strong>{app.emp1_name}</strong></p>
                          <p>{app.emp1_title} ({app.emp1_dates})</p>
                          <p>Supervisor: {app.emp1_supervisor}</p>
                        </div>
                        {app.emp2_name && (
                          <div className="p-3 bg-gray-50 rounded">
                            <p className="font-medium text-amber-600 mb-2">Employer 2</p>
                            <p><strong>{app.emp2_name}</strong></p>
                            <p>{app.emp2_title} ({app.emp2_dates})</p>
                          </div>
                        )}
                        {app.emp3_name && (
                          <div className="p-3 bg-gray-50 rounded">
                            <p className="font-medium text-amber-600 mb-2">Employer 3</p>
                            <p><strong>{app.emp3_name}</strong></p>
                            <p>{app.emp3_title} ({app.emp3_dates})</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Education & Certifications */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <GraduationCap className="w-5 h-5 text-amber-500" /> Education
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Highest Level:</strong> {app.education_level}</p>
                        <p><strong>School:</strong> {app.school1_name}</p>
                        <p><strong>Degree:</strong> {app.school1_degree} in {app.school1_major}</p>
                        <p><strong>Human Services Exp:</strong> {app.has_human_services_experience}</p>
                      </div>
                    </div>

                    {/* References */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Users className="w-5 h-5 text-amber-500" /> References
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Ref 1:</strong> {app.ref1_name} - {app.ref1_phone}</p>
                        <p><strong>Ref 2:</strong> {app.ref2_name} - {app.ref2_phone}</p>
                        <p><strong>Ref 3:</strong> {app.ref3_name} - {app.ref3_phone}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminApplications;
